import { Injectable } from '@angular/core';
import { Observable, of, forkJoin, from } from 'rxjs';
import { catchError, shareReplay, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LottieCacheService {
  private cache = new Map<string, Observable<any>>();

  getAnimation(path: string): Observable<any> {
    if (this.cache.has(path)) {
      return this.cache.get(path);
    }
    const loadAnimation$ = from(fetch(path)).pipe(
      switchMap(response => {
        if (!response.ok) {
          throw new Error(`Failed to load animation: ${response.statusText}`);
        }
        return from(response.json());
      }),
      shareReplay(1),
      catchError(error => {
        console.error(`Error loading Lottie animation from ${path}:`, error);
        this.cache.delete(path);
        throw error;
      })
    );

    this.cache.set(path, loadAnimation$);
    return loadAnimation$;
  }

  preloadAnimations(paths: string[]): void {
    if (paths.length === 0) {
      return;
    }
    const animations$ = paths.map(path =>
      this.getAnimation(path).pipe(
        catchError(error => {
          console.warn(`Failed to preload animation: ${path}`, error);
          return of(null);
        })
      )
    );
    forkJoin(animations$).subscribe();
  }

  clearCache(): void {
    this.cache.clear();
  }

  getCacheSize(): number {
    return this.cache.size;
  }
}
